import React, { useState, useEffect, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Params, useParams } from 'react-router-dom';
import { DataRoomNode } from '../../models/DataRoomNode';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomDocumentsContext from '../DataRoomDocumentsRouter/providers/DataRoomDocuments/DataRoomDocuments.context';
import dataRoomApi from '../../dataRoomApi';
import useAddFolder from '../DataRoomDocumentsRouter/hooks/useAddFolder';
import { Button, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import FolderIcon from 'icons/Folder.icon';
import HomeIcon from 'icons/Home.icon';
import ArrowupIcon from 'icons/Arrowup.icon';
import PluscircleIcon from 'icons/Pluscircle.icon';
import { getHasElementsInArray, sortDataRoomNodes } from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import DataRoomNodeCurrentLocation from 'components/DataRoomNodeCurrentLocation';
import { DATA_ROOM_ROOT_FOLDER } from 'data/global';
import { CurrentLocation } from 'components/DataRoomNodeCurrentLocation/DataRoomNodeCurrentLocation.component';

import './ChooseAFolderInDr.styles.scss';
import './ChooseAFolderInDr.styles.responsive.scss';

type ChooseAFolderInDrProps = {
  className?: string;
  currenntlySelectedNodeId?: string;
  dataRoomId: string;
  hasBackUpLevel?: boolean;
  hasCreateFolder?: boolean;
  disabledFetch?: boolean;
  excludeFolderIds?: string;
  currentLocation?: CurrentLocation;
  onChange: (id: string | null, folder?: string) => void;
};

const ChooseAFolderInDr: React.FC<ChooseAFolderInDrProps> = (props) => {
  const {
    className,
    dataRoomId,
    currenntlySelectedNodeId,
    hasCreateFolder,
    disabledFetch,
    excludeFolderIds,
    currentLocation,
    hasBackUpLevel = true,
    onChange,
  } = props;

  const [areChildrenLoading, setAreChildrenLoading] = useState(false);
  const [documentRoute, setDocumentRoute] = useState<DataRoomNode[]>([]);
  const [currentNodeChildren, setCurrentNodeChildren] = useState<
    Array<DataRoomNode>
  >([]);

  const { popServerError } = useContext(PopNotificationsContext);
  const { setCurrentlyDisplayedNodes } = useContext(DataRoomDocumentsContext);

  const { documentId } = useParams<{ documentId: string }>();
  const {
    params: { sort },
  } = useQueryParams<Params>();

  const classes = classNames('choose-a-folder-in-dr', className);

  const { t } = useTranslation();

  const { addAFolderModal, AddFolderModal } = useAddFolder({
    id: dataRoomId,
    currentlySelectedFolder: currenntlySelectedNodeId,
    onAddFolderSubmit: (node) => {
      if (currenntlySelectedNodeId === node.parentId) {
        setCurrentNodeChildren((prev) => sortDataRoomNodes([node, ...prev]));
      }
      if (currenntlySelectedNodeId === documentId) {
        setCurrentlyDisplayedNodes((prev) =>
          sortDataRoomNodes([node, ...prev], sort),
        );
      }
    },
  });

  const showBackUpLevel = useMemo(
    () => hasBackUpLevel && getHasElementsInArray(documentRoute),
    [documentRoute, hasBackUpLevel],
  );

  const backUpLevel = useMemo(
    () =>
      documentRoute[documentRoute?.length - 2] || {
        _id: null,
        name: t(DATA_ROOM_ROOT_FOLDER),
      },
    [documentRoute, t],
  );

  useEffect(() => {
    if (disabledFetch) return;

    (async () => {
      try {
        setAreChildrenLoading(true);

        const { data } = await dataRoomApi.nodes.getFolderNodes(
          dataRoomId,
          currenntlySelectedNodeId ? currenntlySelectedNodeId : undefined,
          excludeFolderIds ? excludeFolderIds : undefined,
        );

        if (!currenntlySelectedNodeId) {
          setDocumentRoute([]);
        }
        setCurrentNodeChildren(sortDataRoomNodes(data));
      } catch (e) {
        popServerError(e);
      } finally {
        setAreChildrenLoading(false);
      }
    })();
  }, [
    currenntlySelectedNodeId,
    dataRoomId,
    disabledFetch,
    excludeFolderIds,
    popServerError,
  ]);

  useEffect(() => {
    if (disabledFetch || !currenntlySelectedNodeId) return;

    (async () => {
      try {
        setAreChildrenLoading(true);

        const { data } = await dataRoomApi.nodes.getFolderPath(
          dataRoomId,
          currenntlySelectedNodeId,
        );

        setDocumentRoute(data.reverse());
      } catch (e) {
        popServerError(e);
      } finally {
        setAreChildrenLoading(false);
      }
    })();
  }, [currenntlySelectedNodeId, dataRoomId, disabledFetch, popServerError]);

  return (
    <div className={classes}>
      <DataRoomNodeCurrentLocation
        dataRoomId={dataRoomId}
        currentLocation={currentLocation}
        label={t('currentLocation')}
        onLinkClick={addAFolderModal?.close}
        onCurrentLocationClick={({ parentId, parentName }) =>
          onChange(parentId || undefined, parentName)
        }
      />

      <ul className="choose-a-folder-in-dr__breadcrumps">
        <li onClick={() => onChange(undefined, t(DATA_ROOM_ROOT_FOLDER))}>
          <HomeIcon />
          {t(DATA_ROOM_ROOT_FOLDER)}
        </li>
        {documentRoute.map((node) => (
          <li key={node._id} onClick={() => onChange(node._id, node.name)}>
            <p>{node.name}</p>
          </li>
        ))}
      </ul>
      <ul className="choose-a-folder-in-dr__folders">
        {currentNodeChildren.length
          ? currentNodeChildren.map((node) => (
              <li key={node._id} onClick={() => onChange(node._id, node.name)}>
                <FolderIcon className="folder-icon" />
                <p>{node.name}</p>
              </li>
            ))
          : !areChildrenLoading && (
              <li className="choose-a-folder-in-dr__folders__empty">
                {t('folderDoesNotHaveSubfolders')}
              </li>
            )}
      </ul>

      {(showBackUpLevel || hasCreateFolder) && (
        <div className="choose-a-folder-in-dr__actions">
          {showBackUpLevel && (
            <Button
              variant="link"
              className="action-button"
              icon={ArrowupIcon}
              onClick={() => onChange(backUpLevel._id, backUpLevel.name)}
            >
              {t('backUpALevel')}
            </Button>
          )}

          {hasCreateFolder && (
            <>
              <Button
                variant="link"
                className="action-button"
                icon={PluscircleIcon}
                onClick={addAFolderModal?.open}
              >
                {t('createAFolder')}
              </Button>
              {AddFolderModal}
            </>
          )}
        </div>
      )}
      {areChildrenLoading && <Loader />}
    </div>
  );
};

export default ChooseAFolderInDr;
